/**
 * HsnFixDialog — bulk-fix products that are missing an HSN/SAC code.
 *
 * Lists active products with a blank HSN, pre-fills the offline auto-suggested
 * code (and GST rate when the product is at 0%), and applies them in one pass.
 * Fixes future invoices — it does not retro-edit line items already booked.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { Check, History, Sparkles, Wand2 } from 'lucide-react';
import { database } from '@/db';
import { Product } from '@/db/models/Inventory';
import { updateProduct } from '@/inventory/InventoryCrudService';
import { countHsnBackfill, applyHsnBackfill } from '@/inventory/hsnBackfill';
import { detectHSN } from '@/utils/hsnDatabase';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';

const HSN_REGEX = /^\d{4}$|^\d{6}$|^\d{8}$/;

interface Row {
  product: Product;
  hsn: string;
  suggestedRate: number | null;
  include: boolean;
}

export function HsnFixDialog({
  open,
  onOpenChange,
  storeId,
  onApplied,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
  onApplied?: () => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<number | null>(null);
  const [backfillCount, setBackfillCount] = useState(0);
  const [backfilling, setBackfilling] = useState(false);
  const [backfilled, setBackfilled] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setDone(null);
    try {
      const products = await database.get<Product>('products').query(
        Q.where('store_id', storeId),
        Q.where('is_deleted', false),
        Q.where('is_active', true),
        Q.or(Q.where('hsn_code', null), Q.where('hsn_code', '')),
      ).fetch();
      setRows(products.map((product) => {
        const s = detectHSN(product.name);
        return {
          product,
          hsn: s?.code ?? '',
          suggestedRate: s ? s.gstRate : null,
          include: !!s, // pre-select rows we have a confident suggestion for
        };
      }));
      setBackfillCount(await countHsnBackfill(storeId));
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const n = await applyHsnBackfill(storeId);
      setBackfilled(n);
      setBackfillCount(await countHsnBackfill(storeId));
      onApplied?.();
    } finally {
      setBackfilling(false);
    }
  };

  useEffect(() => { if (open) void load(); }, [open, load]);

  const patch = (id: string, p: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.product.id === id ? { ...r, ...p } : r)));

  const readyCount = useMemo(
    () => rows.filter((r) => r.include && HSN_REGEX.test(r.hsn.trim())).length,
    [rows],
  );

  const applyAll = async () => {
    setSaving(true);
    try {
      let n = 0;
      for (const r of rows) {
        const hsn = r.hsn.trim();
        if (!r.include || !HSN_REGEX.test(hsn)) continue;
        const updates: { hsnCode: string; gstRate?: number } = { hsnCode: hsn };
        // Only set the rate from the suggestion when the product is at 0% (i.e.
        // never overwrite a rate the user deliberately chose).
        if (r.suggestedRate != null && r.product.gstRate === 0) updates.gstRate = r.suggestedRate;
        await updateProduct(r.product, updates);
        n += 1;
      }
      setDone(n);
      onApplied?.();
      await load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Fix missing HSN/SAC codes"
        description="Assign HSN codes to products, then backfill them onto past invoices for filing."
        className="w-[760px]"
      >
        <div className="flex max-h-[70vh] flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
            {loading ? (
              <div className="py-10 text-center text-body text-fg-tertiary">Loading…</div>
            ) : (
              <>
                {rows.length > 0 && (
                  <table className="w-full text-body">
                    <thead>
                      <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                        <th className="w-8 py-1"><span className="sr-only">Include</span></th>
                        <th className="py-1 text-left font-normal">Product</th>
                        <th className="py-1 text-left font-normal">HSN / SAC</th>
                        <th className="py-1 text-left font-normal">Suggestion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const valid = HSN_REGEX.test(r.hsn.trim());
                        return (
                          <tr key={r.product.id} className="border-b border-stroke-hairline/10">
                            <td className="py-2">
                              <input
                                type="checkbox"
                                checked={r.include}
                                onChange={(e) => patch(r.product.id, { include: e.target.checked })}
                                aria-label={`Include ${r.product.name}`}
                              />
                            </td>
                            <td className="py-2 pr-3 text-fg-primary">
                              {r.product.name}
                              {r.product.gstRate === 0 && (
                                <span className="ml-1 text-label text-warning-ink">· 0% GST</span>
                              )}
                            </td>
                            <td className="py-2 pr-3">
                              <Input
                                value={r.hsn}
                                onChange={(e) => patch(r.product.id, { hsn: e.target.value })}
                                inputMode="numeric"
                                placeholder="4/6/8 digits"
                                className={cn('w-28', r.include && !valid && 'border-danger/50')}
                              />
                            </td>
                            <td className="py-2 text-label text-fg-tertiary">
                              {r.suggestedRate != null ? (
                                <span className="inline-flex items-center gap-1 text-accent-ink">
                                  <Sparkles size={11} /> {r.hsn || '—'} · GST {r.suggestedRate}%
                                </span>
                              ) : (
                                <span className="text-fg-tertiary">No confident match — enter manually</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* Backfill past invoices */}
                {backfillCount > 0 && (
                  <div className="rounded-md border border-accent/25 bg-accent/5 px-4 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-body font-medium text-fg-primary">Backfill past invoices</div>
                        <div className="text-label text-fg-tertiary">
                          {backfillCount} booked line item{backfillCount === 1 ? '' : 's'} can inherit HSN from the product catalog. HSN is metadata — tax amounts are unchanged.
                        </div>
                      </div>
                      <Button type="button" variant="secondary" onClick={() => void runBackfill()} disabled={backfilling}>
                        <History size={14} className="mr-1.5" /> {backfilling ? 'Backfilling…' : `Backfill ${backfillCount}`}
                      </Button>
                    </div>
                  </div>
                )}

                {rows.length === 0 && backfillCount === 0 && (
                  <div className="flex flex-col items-center gap-2 py-10 text-center">
                    <Check size={28} className="text-success-ink" />
                    <p className="text-heading text-success-ink">All products and invoices have HSN codes</p>
                    {(done != null || backfilled != null) && (
                      <p className="text-label text-fg-tertiary">
                        {done != null && `Updated ${done} product${done === 1 ? '' : 's'}. `}
                        {backfilled != null && `Backfilled ${backfilled} invoice line${backfilled === 1 ? '' : 's'}.`}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-stroke-hairline/10 px-5 py-3">
            <span className="text-label text-fg-tertiary">
              {rows.length > 0
                ? `${readyCount} product${readyCount === 1 ? '' : 's'} ready to apply`
                : done != null || backfilled != null
                  ? 'Done.'
                  : ''}
            </span>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
              {rows.length > 0 && (
                <Button type="button" onClick={() => void applyAll()} disabled={saving || readyCount === 0}>
                  <Wand2 size={14} className="mr-1.5" /> {saving ? 'Applying…' : `Apply ${readyCount}`}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
